"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import type { User as DbUser } from "@otalytics/shared";
import { api } from "@/lib/api/client";

export interface DbUserState {
  dbUser: DbUser | null;
  isLoading: boolean;
  error: string | null;
  refreshDbUser: () => Promise<void>;
}

const DEFAULT_ERROR = "Failed to load user profile.";

export function useDbUser(
  session: Session | null,
  authEvent?: AuthChangeEvent | null,
): DbUserState {
  const [dbUser, setDbUser] = useState<DbUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastUserId = useRef<string | null>(null);

  const refreshDbUser = useCallback(async (): Promise<void> => {
    if (!session) {
      setDbUser(null);
      setError(null);
      setIsLoading(false);
      lastUserId.current = null;
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await api.get<DbUser>("/api/auth/me");
      setDbUser(data);
      lastUserId.current = data.id;
    } catch (err) {
      setError(err instanceof Error ? err.message : DEFAULT_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (!session) {
      setDbUser(null);
      setError(null);
      setIsLoading(false);
      lastUserId.current = null;
      return;
    }

    const userId = session.user?.id ?? null;
    const shouldFetch =
      lastUserId.current === null ||
      (userId && userId !== lastUserId.current) ||
      authEvent === "SIGNED_IN";

    if (shouldFetch) {
      void refreshDbUser();
    }
  }, [authEvent, refreshDbUser, session]);

  return {
    dbUser,
    isLoading,
    error,
    refreshDbUser,
  };
}
